import { Col, Row } from "@amzn/stencil-react-components/layout";
import { PaginationVariant } from "@amzn/stencil-react-components/pagination";
import { TabBar, TabConfig, TabPanel, TabSwitcher, useTabs } from "@amzn/stencil-react-components/tabs";
import { Text } from "@amzn/stencil-react-components/text";
import { withTooltip } from "@amzn/stencil-react-components/tooltip";
import { IconInformation, IconSize } from "@amzn/stencil-react-icons";
import { Pagination } from "@amzn/uxm-stencil-react-components/lib/src/stencil";
import { AIForMeTool } from "lib/models/aiforme-models";
import React, { FC, useMemo, useState } from "react";

const TOOLS_PER_PAGE = 6;
const FIRST_PAGE = 1;

const IconInformationWithTooltip = withTooltip()(IconInformation);

export interface FeaturedTabConfig {
  label: string;
  tooltip: string;
  tools: AIForMeTool[];
  filterParam: string;
  touchpoint: string;
}

export interface FeaturedToolsTabsProps {
  tabConfigs: FeaturedTabConfig[];
  renderToolGrid: (tools: AIForMeTool[], startIndex: number) => JSX.Element;
}

export const FeaturedToolsTabs: FC<FeaturedToolsTabsProps> = ({ tabConfigs, renderToolGrid }) => {
  // Track current page for each tab by its label
  const [pageByTab, setPageByTab] = useState<Record<string, number>>(() =>
    tabConfigs.reduce((acc, config) => ({ ...acc, [config.label]: FIRST_PAGE }), {})
  );

  const handlePageSelect = (tabLabel: string, page: number) => {
    setPageByTab((prev) => ({ ...prev, [tabLabel]: page }));
  };

  const tabs: TabConfig[] = useMemo(
    () =>
      tabConfigs.map((tab) => ({
        value: tab.label,
        label: (
          <Row alignItems="center" gridGap="8px">
            <IconInformationWithTooltip
              title="Show tooltip"
              tooltipText={tab.tooltip}
              size={IconSize.ExtraSmall}
              color={"var(--st-dt-color-action-primary-bg-default)"}
            />
            <Text>{tab.label}</Text>
          </Row>
        ),
      })),
    [tabConfigs]
  );

  const { tabBarProps, tabSwitcherProps } = useTabs({ tabs });

  return (
    <Col style={{ width: "100%" }} rowGap={24}>
      <Row justifyContent="center" className="featured-tools-section__tab-bar-container">
        <TabBar {...tabBarProps} dataTestComponent="feature-tools-tabs" />
      </Row>

      <TabSwitcher {...tabSwitcherProps}>
        {tabConfigs.map((config) => {
          const currentPage = pageByTab[config.label] || FIRST_PAGE;
          const totalPages = Math.ceil(config.tools.length / TOOLS_PER_PAGE);
          const startIndex = (currentPage - 1) * TOOLS_PER_PAGE;
          const displayedTools = config.tools.slice(startIndex, startIndex + TOOLS_PER_PAGE);

          return (
            <TabPanel key={config.label} value={config.label}>
              <Col rowGap={24} alignItems="center">
                {renderToolGrid(displayedTools, startIndex)}
                {totalPages > 1 && (
                  <Pagination
                    numberOfPages={totalPages}
                    selectedPage={currentPage}
                    onPageSelect={(page) => handlePageSelect(config.label, page)}
                    variant={PaginationVariant.Dotted}
                    touchpoint={`featured-tools-pagination-${config.filterParam}`}
                  />
                )}
              </Col>
            </TabPanel>
          );
        })}
      </TabSwitcher>
    </Col>
  );
};
